"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { supabase } from "@/lib/supabase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import Link from "next/link"
import { Checkbox } from "@/components/ui/checkbox"
import { getEmailDomainLoginRestriction, validateEmailDomain } from "@/lib/settings"

export default function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rememberMe, setRememberMe] = useState(true)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmail, setResetEmail] = useState("")
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMessage, setResetMessage] = useState<string | null>(null)
  const [emailDomainLoginRestricted, setEmailDomainLoginRestricted] = useState(false)
  const router = useRouter()

  useEffect(() => {
    checkEmailDomainRestriction()
  }, [])

  const checkEmailDomainRestriction = async () => {
    const isRestricted = await getEmailDomainLoginRestriction()
    setEmailDomainLoginRestricted(isRestricted)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      if (emailDomainLoginRestricted && !validateEmailDomain(email)) {
        setError("Alleen @procap.nl emailadressen zijn toegestaan voor inloggen.")
        setLoading(false)
        return
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        setError(error.message)
      } else {
        router.push("/dashboard")
      }
    } catch (err: any) {
      setError("Er is een onverwachte fout opgetreden: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetLoading(true)
    setError(null)
    setResetMessage(null)

    try {
      if (emailDomainLoginRestricted && !validateEmailDomain(resetEmail)) {
        setError("Alleen @procap.nl emailadressen zijn toegestaan.")
        setResetLoading(false)
        return
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/verificatie`,
      })

      if (error) {
        setError(error.message)
      } else {
        setResetMessage(
          "Er is een herstel-email verzonden naar je e-mailadres. Het kan een paar minuten duren voor je deze ontvangt. Controleer ook je spambox.",
        )
      }
    } catch (err: any) {
      setError("Er is een onverwachte fout opgetreden: " + err.message)
    } finally {
      setResetLoading(false)
    }
  }

  if (showPasswordReset) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            {!resetMessage && (
              <>
                <CardTitle className="text-2xl font-bold">Wachtwoord herstellen</CardTitle>
                <CardDescription>
                  {emailDomainLoginRestricted
                    ? "Voer je @procap.nl e-mailadres in om een herstel-link te ontvangen."
                    : "Voer je e-mailadres in om een herstel-link te ontvangen."}
                </CardDescription>
              </>
            )}
            {resetMessage && (
              <>
                <CardTitle className="text-2xl font-bold">Email verzonden</CardTitle>
                <CardDescription>Controleer je inbox voor de herstel-link.</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {!resetMessage && (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">E-mailadres</Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    placeholder={emailDomainLoginRestricted ? "email@procap.nl" : "email@example.com"}
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                  {emailDomainLoginRestricted && (
                    <p className="text-xs text-muted-foreground">Alleen @procap.nl emailadressen zijn toegestaan</p>
                  )}
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button type="submit" className="w-full" disabled={resetLoading}>
                  {resetLoading ? "Verzenden..." : "Herstel-email verzenden"}
                </Button>
              </form>
            )}
            {resetMessage && (
              <div className="space-y-4">
                <p className="text-sm text-green-600">{resetMessage}</p>
                <Button
                  onClick={() => {
                    setResetMessage(null)
                    setResetEmail("")
                    setError(null)
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Nieuwe email verzenden
                </Button>
              </div>
            )}
            <div className="mt-4 text-center text-sm">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordReset(false)
                  setResetMessage(null)
                  setResetEmail("")
                  setError(null)
                }}
                className="underline text-blue-600 hover:text-blue-800"
              >
                Terug naar inloggen
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Inloggen</CardTitle>
          <CardDescription>
            {emailDomainLoginRestricted
              ? "Voer je @procap.nl e-mailadres en wachtwoord in om in te loggen op je account."
              : "Voer je e-mailadres en wachtwoord in om in te loggen op je account."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                placeholder={emailDomainLoginRestricted ? "email@procap.nl" : "email@example.com"}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailDomainLoginRestricted && (
                <p className="text-xs text-muted-foreground">Alleen @procap.nl emailadressen zijn toegestaan</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(!!checked)}
                />
                <Label htmlFor="rememberMe">Onthoud mij</Label>
              </div>
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Wachtwoord vergeten?
              </button>
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Inloggen..." : "Inloggen"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            Nog geen account?{" "}
            <Link href="/aanmelden" className="underline">
              Meld je aan
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
